import { Check, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

function PaymentLinkEditor({ pro, onSaved }) {
  const [link, setLink] = useState(pro.professional_profile?.payment_link || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/professionals/${pro.user_id}/payment-link`, { payment_link: link.trim() });
      toast.success("Liga de pago guardada");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="w-full flex flex-wrap items-center gap-2 pt-3 border-t mt-1">
      <Label className="text-xs text-muted-foreground shrink-0">Liga de pago (Stripe)</Label>
      <Input
        value={link}
        onChange={(e) => setLink(e.target.value)}
        placeholder="https://buy.stripe.com/..."
        className="flex-1 min-w-[220px] h-9 text-sm"
        data-testid={`payment-link-input-${pro.user_id}`}
      />
      <Button
        size="sm"
        variant="outline"
        className="rounded-full"
        onClick={save}
        disabled={saving || link === (pro.professional_profile?.payment_link || "")}
        data-testid={`payment-link-save-${pro.user_id}`}
      >
        {saving ? "Guardando..." : "Guardar"}
      </Button>
    </div>
  );
}

function PendingProCard({ pro, onAct }) {
  return (
    <div className="border rounded-2xl bg-white p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`pending-pro-${pro.user_id}`}>
      <div>
        <p className="font-semibold">
          {(pro.professional_profile?.titulo || "")} {pro.name}
        </p>
        <p className="text-sm text-muted-foreground">
          {pro.email} · {pro.professional_profile?.especialidad || "Sin especialidad"} · Cédula:{" "}
          {pro.professional_profile?.cedula || "—"}
        </p>
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="rounded-full" onClick={() => onAct(pro.user_id, "approve")} data-testid={`approve-pro-${pro.user_id}`}>
          <Check className="h-4 w-4 mr-1" strokeWidth={1.5} /> Aprobar
        </Button>
        <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => onAct(pro.user_id, "reject")} data-testid={`reject-pro-${pro.user_id}`}>
          <X className="h-4 w-4 mr-1" strokeWidth={1.5} /> Rechazar
        </Button>
      </div>
    </div>
  );
}

function ApprovedProCard({ pro, onAct, onSaved }) {
  const approved = pro.approval_status === "approved";
  return (
    <div className="border rounded-2xl bg-white p-5 flex flex-wrap items-center justify-between gap-4">
      <div>
        <p className="font-semibold">
          {(pro.professional_profile?.titulo || "")} {pro.name}
        </p>
        <p className="text-sm text-muted-foreground">
          {pro.email}
          {pro.whatsapp ? ` · WhatsApp: ${pro.whatsapp}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Badge
          variant="outline"
          className={`rounded-full ${approved ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}`}
        >
          {approved ? "Aprobado" : "Rechazado"}
        </Badge>
        {approved ? (
          <Button size="sm" variant="outline" className="rounded-full text-destructive" onClick={() => onAct(pro.user_id, "reject")}>
            Revocar
          </Button>
        ) : (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onAct(pro.user_id, "approve")}>
            Aprobar
          </Button>
        )}
      </div>
      {approved && <PaymentLinkEditor pro={pro} onSaved={onSaved} />}
    </div>
  );
}

export function ProfessionalsTab() {
  const [pros, setPros] = useState([]);
  const load = () => api.get("/admin/professionals").then((r) => setPros(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const act = async (userId, action) => {
    try {
      await api.post(`/admin/professionals/${userId}/${action}`);
      toast.success(action === "approve" ? "Profesional aprobado" : "Profesional rechazado");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const pending = pros.filter((p) => p.approval_status === "pending");
  const others = pros.filter((p) => p.approval_status !== "pending");

  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-heading font-semibold mb-4">Pendientes de aprobación ({pending.length})</h3>
        {pending.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-pending-pros">No hay profesionales pendientes.</p>}
        <div className="grid gap-3">
          {pending.map((p) => (
            <PendingProCard key={p.user_id} pro={p} onAct={act} />
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-heading font-semibold mb-4">Todos los profesionales</h3>
        <div className="grid gap-3">
          {others.map((p) => (
            <ApprovedProCard key={p.user_id} pro={p} onAct={act} onSaved={load} />
          ))}
        </div>
      </div>
    </div>
  );
}
